import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  AlertCircle,
  Building2,
  Check,
  CheckCircle2,
  Circle,
  CircleDot,
  Clock3,
  Droplets,
  Eye,
  Info,
  Mail,
  Pencil,
  RotateCcw,
  Save,
  Send,
  UserRound,
  X,
} from "lucide-react";
import { Button } from "@/ui/components";
import equilibriumEmailLogo from "@/assets/logos/ee-logo-150-pixels.png";
import linfinityEmailLogo from "@/assets/logos/linfinity-logo-600.svg";
import {
  projectClientNotificationService,
  type ProjectClientNotificationDiscipline,
  type ProjectClientNotificationHistoryItem,
  type ProjectClientNotificationProgressItem,
  type ProjectClientNotificationTemplate,
  type ProjectClientNotificationWorkspace,
} from "@/services/projectClientNotificationService";
import "./ProjectClientNotifications.css";

type Props = {
  projectId: number;
  archived?: boolean;
};

type LocalDraft = {
  messageText: string;
  personalNote: string;
  includeServiceMention: boolean;
};

const DISCIPLINES: Array<{
  key: ProjectClientNotificationDiscipline;
  label: string;
  subtitle: string;
}> = [
  { key: "structural", label: "Structural", subtitle: "Owned by Yuan Lin" },
  { key: "civil", label: "Civil / drainage", subtitle: "Owned by Wilson Du" },
];

const EMAIL_BRANDS: Record<ProjectClientNotificationDiscipline, {
  companyName: string;
  engineerName: string;
  engineerTitle: string;
  disciplineLabel: string;
  logoSrc: string;
  email: string;
  phone: string;
  website: string;
}> = {
  structural: {
    companyName: "Linfinity Engineering Pty Ltd",
    engineerName: "Yuan Lin",
    engineerTitle: "Director / Structural Engineer (PE0000649, MIEAust)",
    disciplineLabel: "Structural engineering",
    logoSrc: linfinityEmailLogo,
    email: "yl@linfinityeng.com",
    phone: "0468 886 926",
    website: "linfinityeng.com",
  },
  civil: {
    companyName: "Equilibrium Engineering Pty Ltd",
    engineerName: "Wilson Du",
    engineerTitle: "Director / Civil Engineer (PE-0000554, MIEAust)",
    disciplineLabel: "Civil / drainage engineering",
    logoSrc: equilibriumEmailLogo,
    email: "wdu@eqeng.com.au",
    phone: "0421 900 119",
    website: "eqeng.com.au",
  },
};

function formatDate(value?: string | null) {
  if (!value) return "Date unavailable";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Date unavailable";
  return new Intl.DateTimeFormat("en-AU", {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(date);
}

function formatDateTime(value?: string | null) {
  if (!value) return "Time unavailable";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Time unavailable";
  return new Intl.DateTimeFormat("en-AU", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
}

function sentHistory(history: ProjectClientNotificationHistoryItem[]) {
  return history.filter((item) => item.status.toLowerCase() === "sent");
}

export function formatProgressActor(
  value: string | null | undefined,
  ownerName?: string
) {
  const actor = value?.trim();
  if (!actor) return "Unknown";
  if (!actor.includes("@")) return actor;

  const localPart = actor.split("@")[0].toLowerCase();
  if (ownerName) {
    const nameParts = ownerName.toLowerCase().match(/[a-z0-9]+/g) || [];
    const initials = nameParts.map((part) => part[0]).join("");
    const firstInitialAndSurname = nameParts.length > 1
      ? `${nameParts[0][0]}${nameParts[nameParts.length - 1]}`
      : initials;
    const compactName = nameParts.join("");
    if ([initials, firstInitialAndSurname, compactName].includes(localPart)) return ownerName;
  }

  return localPart
    .split(/[._-]+/)
    .filter(Boolean)
    .map((part) => `${part[0].toUpperCase()}${part.slice(1)}`)
    .join(" ") || actor;
}

export function getCurrentTemplateKey(
  templates: ProjectClientNotificationTemplate[],
  progress: ProjectClientNotificationProgressItem[],
  discipline: ProjectClientNotificationDiscipline
) {
  const disciplineTemplates = templates
    .filter((template) => template.discipline === discipline)
    .sort((a, b) => a.order - b.order);
  const currentStageKey = progress.find((item) => item.discipline === discipline)?.currentStageKey;
  return disciplineTemplates.some((template) => template.key === currentStageKey)
    ? currentStageKey || null
    : disciplineTemplates[0]?.key || null;
}

export function getStageStatus(
  templates: ProjectClientNotificationTemplate[],
  template: ProjectClientNotificationTemplate,
  currentStageKey: string | null
): "complete" | "current" | "upcoming" {
  const disciplineTemplates = templates
    .filter((item) => item.discipline === template.discipline)
    .sort((a, b) => a.order - b.order);
  const currentTemplate = disciplineTemplates.find((item) => item.key === currentStageKey) || disciplineTemplates[0];
  if (template.order < currentTemplate.order) return "complete";
  if (template.key === currentTemplate.key) return "current";
  return "upcoming";
}

function chooseInitialTemplate(workspace: ProjectClientNotificationWorkspace) {
  const latestSent = sentHistory(workspace.history)[0];
  const preferredDiscipline = latestSent?.discipline || "structural";
  return getCurrentTemplateKey(workspace.templates, workspace.progress || [], preferredDiscipline)
    || workspace.templates[0]?.key
    || "";
}

function EmailMessagePreview({ value }: { value: string }) {
  return (
    <>
      {value.replace(/\r\n/g, "\n").split(/\n{2,}/).map((block, index) => {
        const lines = block.split("\n").map((line) => line.trim()).filter(Boolean);
        const isBulletList = lines.length > 0 && lines.every((line) => /^[•*-]\s+/.test(line));
        if (isBulletList) {
          return (
            <ul className="project-client-updates__email-message-list" key={`list-${index}`}>
              {lines.map((line) => {
                const content = line.replace(/^[•*-]\s+/, "");
                const labelled = content.match(/^([^:]{1,40}):\s*(.+)$/);
                return (
                  <li key={line}>
                    {labelled ? <><strong>{labelled[1]}:</strong> {labelled[2]}</> : content}
                  </li>
                );
              })}
            </ul>
          );
        }
        return <p key={`paragraph-${index}`}>{block}</p>;
      })}
    </>
  );
}

function EmailBrandPreview({ discipline }: { discipline: ProjectClientNotificationDiscipline }) {
  const brand = EMAIL_BRANDS[discipline];
  return (
    <header className={`project-client-updates__email-brand is-${discipline}`}>
      <img src={brand.logoSrc} alt={brand.companyName} />
      <span>Client project update</span>
      <strong>{brand.companyName}</strong>
      <small>{brand.disciplineLabel} · {brand.engineerName}</small>
    </header>
  );
}

function EmailSignaturePreview({ discipline }: { discipline: ProjectClientNotificationDiscipline }) {
  const brand = EMAIL_BRANDS[discipline];
  return (
    <footer className={`project-client-updates__email-signature is-${discipline}`}>
      <p>{discipline === "civil" ? "Should you have further queries, please contact us." : "Please feel free to contact us for further information."}</p>
      <span>Sincerely,</span>
      <strong>{brand.engineerName}</strong>
      <small>{brand.engineerTitle}</small>
      <img src={brand.logoSrc} alt={brand.companyName} />
      <small><b>M:</b> {brand.phone}</small>
      <small><b>E:</b> {brand.email}</small>
      <small><b>W:</b> {brand.website}</small>
      <small><b>Company:</b> {brand.companyName}</small>
    </footer>
  );
}

function EmailTimelinePreview({
  templates,
  currentStageKeys,
  discipline,
}: {
  templates: ProjectClientNotificationTemplate[];
  currentStageKeys: Partial<Record<ProjectClientNotificationDiscipline, string>>;
  discipline: ProjectClientNotificationDiscipline;
}) {
  const disciplineMeta = DISCIPLINES.find((item) => item.key === discipline)!;
  const brand = EMAIL_BRANDS[discipline];
  return (
    <div className="project-client-updates__email-timeline" aria-label={`${brand.disciplineLabel} progress included in email`}>
      <span className="project-client-updates__email-timeline-eyebrow">{brand.disciplineLabel} progress</span>
      <div className="project-client-updates__email-lane">
        <strong>{disciplineMeta.label}</strong>
        <small>{brand.companyName} · {brand.engineerName}</small>
        <div>
          {templates
            .filter((template) => template.discipline === discipline)
            .sort((a, b) => a.order - b.order)
            .map((template) => {
              const status = getStageStatus(templates, template, currentStageKeys[discipline] || null);
              return (
                <div className={`project-client-updates__email-stage is-${status}`} key={template.key}>
                  {status === "complete" ? <CheckCircle2 size={17} /> : status === "current" ? <CircleDot size={17} /> : <Circle size={17} />}
                  <span><strong>{template.label}</strong><small>{status}</small></span>
                </div>
              );
            })}
        </div>
      </div>
    </div>
  );
}

function draftStorageKey(projectId: number, templateKey: string) {
  return `pmapp:client-update-draft:${projectId}:${templateKey}`;
}

function readDraft(projectId: number, template: ProjectClientNotificationTemplate): LocalDraft {
  try {
    const raw = window.localStorage.getItem(draftStorageKey(projectId, template.key));
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<LocalDraft>;
      return {
        messageText: typeof parsed.messageText === "string" ? parsed.messageText : template.defaultMessage,
        personalNote: typeof parsed.personalNote === "string" ? parsed.personalNote : "",
        includeServiceMention: parsed.includeServiceMention !== false,
      };
    }
  } catch {
    // A malformed or blocked browser draft should never prevent sending an update.
  }
  return {
    messageText: template.defaultMessage,
    personalNote: "",
    includeServiceMention: true,
  };
}

const ProjectClientNotifications: React.FC<Props> = ({ projectId, archived = false }) => {
  const [workspace, setWorkspace] = useState<ProjectClientNotificationWorkspace | null>(null);
  const [selectedKey, setSelectedKey] = useState("");
  const [messageText, setMessageText] = useState("");
  const [personalNote, setPersonalNote] = useState("");
  const [includeServiceMention, setIncludeServiceMention] = useState(true);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [updatingStageKey, setUpdatingStageKey] = useState<string | null>(null);
  const [editingDetailKey, setEditingDetailKey] = useState<string | null>(null);
  const [detailDraft, setDetailDraft] = useState("");
  const [savingDetailKey, setSavingDetailKey] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [draftNotice, setDraftNotice] = useState<string | null>(null);
  const lastStageTapRef = useRef<{ key: string; at: number } | null>(null);
  const stageChangeInFlightRef = useRef(false);

  const loadWorkspace = async (preserveSelection = false) => {
    setLoading(true);
    setError(null);
    try {
      const result = await projectClientNotificationService.getWorkspace(projectId);
      setWorkspace(result);
      setSelectedKey((current) => preserveSelection && result.templates.some((item) => item.key === current)
        ? current
        : chooseInitialTemplate(result));
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : "Could not load client update stages.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadWorkspace();
  }, [projectId]);

  const selectedTemplate = useMemo(
    () => workspace?.templates.find((template) => template.key === selectedKey) || null,
    [workspace?.templates, selectedKey]
  );

  useEffect(() => {
    if (!selectedTemplate) return;
    const draft = readDraft(projectId, selectedTemplate);
    setMessageText(draft.messageText);
    setPersonalNote(draft.personalNote);
    setIncludeServiceMention(draft.includeServiceMention);
    setConfirming(false);
    setDraftNotice(null);
    setNotice(null);
  }, [projectId, selectedTemplate?.key]);

  const successfulHistory = useMemo(() => sentHistory(workspace?.history || []), [workspace?.history]);
  const latestHistory = workspace?.history?.[0] || null;
  const currentStageKeys = useMemo(() => Object.fromEntries(
    DISCIPLINES.map((discipline) => [
      discipline.key,
      getCurrentTemplateKey(workspace?.templates || [], workspace?.progress || [], discipline.key) || "",
    ])
  ) as Record<ProjectClientNotificationDiscipline, string>, [workspace?.templates, workspace?.progress]);
  const emailCurrentStageKeys = useMemo(() => selectedTemplate
    ? { ...currentStageKeys, [selectedTemplate.discipline]: selectedTemplate.key }
    : currentStageKeys, [currentStageKeys, selectedTemplate]);

  const selectTemplate = (template: ProjectClientNotificationTemplate) => {
    setSelectedKey(template.key);
    setError(null);
  };

  const setStageCurrent = async (template: ProjectClientNotificationTemplate) => {
    if (archived || stageChangeInFlightRef.current) return;
    stageChangeInFlightRef.current = true;
    setUpdatingStageKey(template.key);
    setSelectedKey(template.key);
    setError(null);
    setNotice(null);
    try {
      const result = await projectClientNotificationService.setCurrentStage(
        projectId,
        template.discipline,
        template.key
      );
      setWorkspace((current) => current ? { ...current, progress: result.progress } : current);
      setNotice(`${template.label} is now the current ${template.discipline === "civil" ? "civil / drainage" : "structural"} stage.`);
    } catch (stageError) {
      setError(stageError instanceof Error ? stageError.message : "The current stage could not be updated.");
    } finally {
      stageChangeInFlightRef.current = false;
      setUpdatingStageKey(null);
    }
  };

  const handleStageClick = (template: ProjectClientNotificationTemplate) => {
    selectTemplate(template);
    const now = Date.now();
    const lastTap = lastStageTapRef.current;
    if (lastTap?.key === template.key && now - lastTap.at <= 450) {
      lastStageTapRef.current = null;
      const disciplineTemplates = (workspace?.templates || [])
        .filter((item) => item.discipline === template.discipline)
        .sort((a, b) => a.order - b.order);
      const stageIndex = disciplineTemplates.findIndex((item) => item.key === template.key);
      const isCurrent = currentStageKeys[template.discipline] === template.key;
      const targetTemplate = isCurrent && stageIndex > 0
        ? disciplineTemplates[stageIndex - 1]
        : template;

      if (isCurrent && stageIndex === 0) {
        setNotice(`${template.label} is already the first ${template.discipline === "civil" ? "civil / drainage" : "structural"} stage.`);
        return;
      }

      void setStageCurrent(targetTemplate);
      return;
    }
    lastStageTapRef.current = { key: template.key, at: now };
  };

  const startEditingDetail = (template: ProjectClientNotificationTemplate) => {
    selectTemplate(template);
    setEditingDetailKey(template.key);
    setDetailDraft(template.shortDescription);
    setError(null);
    setNotice(null);
  };

  const saveStageDetail = async (template: ProjectClientNotificationTemplate) => {
    const detailText = detailDraft.trim();
    if (!detailText || savingDetailKey) return;
    const shouldRefreshMessage = selectedKey === template.key
      && messageText.trim() === template.defaultMessage.trim();
    setSavingDetailKey(template.key);
    setError(null);
    setNotice(null);
    try {
      const result = await projectClientNotificationService.saveStageDetail(
        projectId,
        template.discipline,
        template.key,
        detailText
      );
      setWorkspace((current) => current ? { ...current, templates: result.templates } : current);
      const updatedTemplate = result.templates.find((item) => item.key === template.key);
      if (shouldRefreshMessage && updatedTemplate) setMessageText(updatedTemplate.defaultMessage);
      setEditingDetailKey(null);
      setNotice(`${template.label} detail saved for this project.`);
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : "The stage detail could not be saved.");
    } finally {
      setSavingDetailKey(null);
    }
  };

  const resetTemplate = () => {
    if (!selectedTemplate) return;
    try {
      window.localStorage.removeItem(draftStorageKey(projectId, selectedTemplate.key));
    } catch {
      // Resetting the visible composer still succeeds when browser storage is unavailable.
    }
    setMessageText(selectedTemplate.defaultMessage);
    setPersonalNote("");
    setIncludeServiceMention(true);
    setConfirming(false);
    setDraftNotice("Template restored.");
  };

  const saveDraft = () => {
    if (!selectedTemplate) return;
    try {
      window.localStorage.setItem(draftStorageKey(projectId, selectedTemplate.key), JSON.stringify({
        messageText,
        personalNote,
        includeServiceMention,
      } satisfies LocalDraft));
      setDraftNotice("Draft saved in this browser.");
      setError(null);
    } catch {
      setError("This browser could not save the draft.");
    }
  };

  const sendUpdate = async () => {
    if (!selectedTemplate || !messageText.trim()) return;
    setSending(true);
    setError(null);
    setNotice(null);
    try {
      const clientRequestId = typeof crypto.randomUUID === "function"
        ? crypto.randomUUID()
        : "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (token) => {
          const randomValue = Math.floor(Math.random() * 16);
          const value = token === "x" ? randomValue : (randomValue & 0x3) | 0x8;
          return value.toString(16);
        });
      await projectClientNotificationService.send(projectId, {
        templateKey: selectedTemplate.key,
        messageText: messageText.trim(),
        personalNote: personalNote.trim() || null,
        includeServiceMention,
        clientRequestId,
      });
      window.localStorage.removeItem(draftStorageKey(projectId, selectedTemplate.key));
      setNotice(`${selectedTemplate.label} update sent to ${workspace?.project.clientEmail}.`);
      setConfirming(false);
      await loadWorkspace(true);
    } catch (sendError) {
      setError(sendError instanceof Error ? sendError.message : "The client update could not be sent.");
      setConfirming(false);
      await loadWorkspace(true);
    } finally {
      setSending(false);
    }
  };

  if (loading && !workspace) {
    return (
      <section className="project-client-updates" aria-label="Client project updates">
        <div className="project-client-updates__loading">
          <Clock3 size={18} aria-hidden="true" />
          Loading client update stages…
        </div>
      </section>
    );
  }

  if (!workspace) {
    return (
      <section className="project-client-updates" aria-label="Client project updates">
        <div className="project-client-updates__state project-client-updates__state--error">
          <AlertCircle size={19} aria-hidden="true" />
          <div>
            <strong>Client updates are unavailable</strong>
            <span>{error || "The notification workspace could not be loaded."}</span>
          </div>
          <Button variant="secondary" onClick={() => void loadWorkspace()}>Retry</Button>
        </div>
      </section>
    );
  }

  return (
    <section className="project-client-updates" aria-labelledby="client-update-title">
      <header className="project-client-updates__header">
        <div>
          <div className="project-client-updates__eyebrow">Client communication</div>
          <h2 id="client-update-title">Project progress timeline</h2>
          <p>Choose the milestone that best reflects the work, personalize the message, then send it manually.</p>
        </div>
        <div className="project-client-updates__last-update">
          <span>Last notification</span>
          {latestHistory ? (
            <>
              <strong>{latestHistory.stageLabel}</strong>
              <small>{formatDate(latestHistory.sentAt || latestHistory.attemptedAt)}</small>
            </>
          ) : (
            <strong>No client updates yet</strong>
          )}
        </div>
      </header>

      {archived ? (
        <div className="project-client-updates__notice project-client-updates__notice--warning">
          <Info size={17} aria-hidden="true" /> Restore this project before sending a client update.
        </div>
      ) : null}
      {error ? (
        <div className="project-client-updates__notice project-client-updates__notice--error" role="alert">
          <AlertCircle size={17} aria-hidden="true" /> {error}
        </div>
      ) : null}
      {notice ? (
        <div className="project-client-updates__notice project-client-updates__notice--success" role="status">
          <Check size={17} aria-hidden="true" /> {notice}
        </div>
      ) : null}

      <div className="project-client-updates__workspace">
        <div className="project-client-updates__timeline-panel">
          <div className="project-client-updates__legend" aria-label="Timeline status legend">
            <span><CheckCircle2 size={16} /> Complete</span>
            <span><CircleDot className="project-client-updates__legend-current" size={16} /> Current</span>
            <span><Circle size={16} /> Upcoming</span>
          </div>

          {DISCIPLINES.map((discipline) => {
            const disciplineTemplates = workspace.templates
              .filter((template) => template.discipline === discipline.key)
              .sort((a, b) => a.order - b.order);
            const disciplineProgress = workspace.progress.find((item) => item.discipline === discipline.key);
            const latestDisciplineSent = successfulHistory.find((item) => item.discipline === discipline.key);
            return (
              <div className="project-client-updates__lane" key={discipline.key}>
                <div className={`project-client-updates__discipline project-client-updates__discipline--${discipline.key}`}>
                  <span className="project-client-updates__discipline-icon">
                    {discipline.key === "structural" ? <Building2 size={25} /> : <Droplets size={25} />}
                  </span>
                  <strong>{discipline.label}</strong>
                  <small>{discipline.subtitle}</small>
                </div>
                <div className="project-client-updates__stages">
                  {disciplineTemplates.map((template, stageIndex) => {
                    const status = getStageStatus(workspace.templates, template, currentStageKeys[discipline.key]);
                    const isComplete = status === "complete";
                    const isCurrent = status === "current";
                    const isSelected = selectedKey === template.key;
                    const isEditing = editingDetailKey === template.key;
                    const previousTemplate = stageIndex > 0 ? disciplineTemplates[stageIndex - 1] : null;
                    const stageInteractionHint = isCurrent
                      ? previousTemplate
                        ? `Double-click to revert to ${previousTemplate.label}.`
                        : "This is the first stage."
                      : "Double-click to make current.";
                    return (
                      <div className={`project-client-updates__stage-shell${isSelected ? " is-selected" : ""}`} key={template.key}>
                        <button
                          type="button"
                          className={`project-client-updates__stage is-${status}${isSelected ? " is-selected" : ""}`}
                          onClick={() => handleStageClick(template)}
                          aria-pressed={isSelected}
                          aria-label={`${template.label}, ${status}. ${stageInteractionHint}`}
                          title={stageInteractionHint}
                          disabled={updatingStageKey === template.key}
                        >
                          <span className={`project-client-updates__stage-marker${isComplete ? " is-complete" : isCurrent ? " is-current" : ""}`}>
                            {isComplete ? <CheckCircle2 size={22} /> : isCurrent ? <CircleDot size={22} /> : <Circle size={22} />}
                          </span>
                          <span className="project-client-updates__stage-copy">
                            <strong>{template.label}</strong>
                            <small>{template.shortDescription}</small>
                          </span>
                          <span className="project-client-updates__stage-meta">
                            {isComplete ? <em>Complete</em> : isCurrent ? <em>Current</em> : "Upcoming"}
                          </span>
                        </button>
                        {!isEditing ? (
                          <button
                            type="button"
                            className="project-client-updates__stage-detail-action"
                            onClick={() => startEditingDetail(template)}
                            aria-label={`Edit ${template.label} detail`}
                            title="Edit stage detail"
                          >
                            <Pencil size={12} />
                          </button>
                        ) : (
                          <div className="project-client-updates__stage-detail-editor">
                            <label htmlFor={`stage-detail-${template.key}`}>Project-specific stage detail</label>
                            <textarea
                              id={`stage-detail-${template.key}`}
                              value={detailDraft}
                              onChange={(event) => setDetailDraft(event.target.value)}
                              rows={3}
                              maxLength={2000}
                              autoFocus
                            />
                            <small>{detailDraft.length.toLocaleString()} / 2,000 · Used in the timeline and email update.</small>
                            <div>
                              <button
                                type="button"
                                onClick={() => setDetailDraft(template.defaultShortDescription || template.shortDescription)}
                                disabled={savingDetailKey === template.key}
                              >
                                <RotateCcw size={12} /> Use default
                              </button>
                              <button
                                type="button"
                                onClick={() => setEditingDetailKey(null)}
                                disabled={savingDetailKey === template.key}
                              >
                                <X size={12} /> Cancel
                              </button>
                              <button
                                type="button"
                                className="is-primary"
                                onClick={() => void saveStageDetail(template)}
                                disabled={!detailDraft.trim() || savingDetailKey === template.key}
                              >
                                <Save size={12} /> {savingDetailKey === template.key ? "Saving…" : "Save detail"}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {disciplineProgress?.updatedAt || latestDisciplineSent ? (
                    <div className="project-client-updates__lane-audit" aria-label={`${discipline.label} activity`}>
                      {disciplineProgress?.updatedAt ? (
                        <span>
                          Last updated by {formatProgressActor(disciplineProgress.updatedBy, disciplineTemplates[0]?.ownerName)} on {formatDate(disciplineProgress.updatedAt)}
                        </span>
                      ) : null}
                      {latestDisciplineSent ? (
                        <button type="button" className="project-client-updates__history-link" onClick={() => setShowHistory(true)}>
                          Sent by {latestDisciplineSent.senderName} on {formatDate(latestDisciplineSent.sentAt)}
                        </button>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              </div>
            );
          })}
          <div className="project-client-updates__timeline-help">
            <Info size={17} aria-hidden="true" />
            Double-click a milestone to make it current. Double-click the current milestone to move back one stage. Earlier milestones become complete; sending an update keeps the sent milestone current.
          </div>
        </div>

        {selectedTemplate ? (
          <div className="project-client-updates__composer">
            <div className="project-client-updates__composer-heading">
              <div>
                <span>Selected milestone</span>
                <h3>{selectedTemplate.label}</h3>
                <small>Owned by {selectedTemplate.ownerName}</small>
              </div>
              <span className={`project-client-updates__discipline-badge is-${selectedTemplate.discipline}`}>
                {selectedTemplate.discipline === "civil" ? "Civil / drainage" : "Structural"}
              </span>
            </div>

            <div className="project-client-updates__recipient">
              <UserRound size={18} aria-hidden="true" />
              <span>
                <small>Recipient</small>
                <strong>{workspace.project.clientName || "Client"}</strong>
                <em>{workspace.project.clientEmail || "Client email not set"}</em>
              </span>
            </div>

            <label className="project-client-updates__field">
              <span>
                <strong>Predefined message</strong>
                <button type="button" onClick={resetTemplate}><RotateCcw size={14} /> Reset to template</button>
              </span>
              <textarea
                value={messageText}
                onChange={(event) => {
                  setMessageText(event.target.value);
                  setConfirming(false);
                  setDraftNotice(null);
                }}
                rows={10}
                maxLength={6000}
              />
              <small>{messageText.length.toLocaleString()} / 6,000</small>
            </label>

            <label className="project-client-updates__field">
              <span><strong>Personal note</strong><em>Optional</em></span>
              <textarea
                className="project-client-updates__personal-note"
                value={personalNote}
                onChange={(event) => {
                  setPersonalNote(event.target.value);
                  setConfirming(false);
                  setDraftNotice(null);
                }}
                rows={3}
                maxLength={1500}
                placeholder="Add project-specific context for the client…"
              />
              <small>This note appears above the predefined message.</small>
            </label>

            <div className="project-client-updates__preview-heading">
              <strong>Client email preview</strong>
              <button type="button" onClick={() => setShowPreview((current) => !current)}>
                <Eye size={15} /> {showPreview ? "Hide preview" : "Preview email"}
              </button>
            </div>
            <div className="project-client-updates__preview">
              <div><span>Subject</span><strong>{selectedTemplate.subject}</strong></div>
              <div><span>To</span><strong>{workspace.project.clientEmail || "Client email not set"}</strong></div>
              {showPreview ? (
                <div className="project-client-updates__preview-body">
                  <EmailBrandPreview discipline={selectedTemplate.discipline} />
                  {personalNote ? <aside>{personalNote}</aside> : null}
                  <EmailMessagePreview value={messageText} />
                  <EmailTimelinePreview
                    templates={workspace.templates}
                    currentStageKeys={emailCurrentStageKeys}
                    discipline={selectedTemplate.discipline}
                  />
                  {includeServiceMention ? (
                    <div className="project-client-updates__email-service-mention">
                      <strong>Related service</strong>
                      <span>{selectedTemplate.serviceMentionText}</span>
                    </div>
                  ) : null}
                  <EmailSignaturePreview discipline={selectedTemplate.discipline} />
                </div>
              ) : null}
            </div>

            <label className="project-client-updates__service-mention">
              <input
                type="checkbox"
                checked={includeServiceMention}
                onChange={(event) => {
                  setIncludeServiceMention(event.target.checked);
                  setConfirming(false);
                  setDraftNotice(null);
                }}
              />
              <span>
                <strong>Relevant service mention</strong>
                <small>{selectedTemplate.serviceMentionText}</small>
              </span>
            </label>

            {draftNotice ? <div className="project-client-updates__draft-notice">{draftNotice}</div> : null}

            <div className="project-client-updates__actions">
              {confirming ? (
                <div className="project-client-updates__confirm" role="group" aria-label="Confirm client update">
                  <span><Mail size={17} /> Send this email now?</span>
                  <Button variant="secondary" onClick={() => setConfirming(false)} disabled={sending}>Cancel</Button>
                  <Button variant="primary" onClick={() => void sendUpdate()} disabled={sending}>
                    {sending ? "Sending…" : "Confirm send"}
                  </Button>
                </div>
              ) : (
                <>
                  <Button variant="secondary" onClick={saveDraft} disabled={sending}>
                    <Save size={16} /> Save draft
                  </Button>
                  <Button
                    variant="primary"
                    onClick={() => setConfirming(true)}
                    disabled={sending || archived || !workspace.project.clientEmail || !messageText.trim()}
                  >
                    <Send size={16} /> Send client update
                  </Button>
                </>
              )}
            </div>
          </div>
        ) : null}
      </div>

      <div className="project-client-updates__history-summary">
        <button type="button" onClick={() => setShowHistory((current) => !current)}>
          <Clock3 size={16} /> {showHistory ? "Hide send history" : `View send history (${workspace.history.length})`}
        </button>
      </div>
      {showHistory ? (
        <div className="project-client-updates__history">
          {workspace.history.length ? workspace.history.map((item) => (
            <div className="project-client-updates__history-row" key={item.notificationId}>
              <span className={`project-client-updates__history-status is-${item.status.toLowerCase()}`}>
                {item.status.toLowerCase() === "sent" ? <CheckCircle2 size={17} /> : item.status.toLowerCase() === "failed" ? <AlertCircle size={17} /> : <Clock3 size={17} />}
                {item.status}
              </span>
              <span><strong>{item.stageLabel}</strong><small>{item.discipline === "civil" ? "Civil / drainage" : "Structural"}</small></span>
              <span><strong>{item.senderName}</strong><small>to {item.recipientEmail}</small></span>
              <time>{formatDateTime(item.sentAt || item.failedAt || item.attemptedAt)}</time>
            </div>
          )) : <div className="project-client-updates__history-empty">No client updates have been attempted for this project.</div>}
        </div>
      ) : null}
    </section>
  );
};

export default ProjectClientNotifications;
